import { useEffect, useState } from "react";
import { CertificateRegistryService } from "../../../Component/Api/VerificationAgency/CertificateRegistry/CertificateRegistryService";
import { DataTable } from "../../../Component/Common/Components/DataTable/DataTable";
import { CertificateRegistry } from "../../../Component/Model/Dto/CertificateRegistry/CertificateRegistry";
import { ColumnSortOption } from "../../../Component/Model/Dto/ColumnSortOption";
import { EntriesPerPage } from '../../../Component/Model/Table/Table';
import { CertificateRegistryTableMapper } from "./CertificateRegistryTable";
import { ScorecardTypeSelectionModal } from "./ScorecardTypeSelectionModal";
import { CertificateRegistryModal } from "./CertificateRegistryModal";
import { ScorecardType } from "../../../Component/Model/Dto/StaticData";
import { PortalDocumentService } from "../../../Component/Api/PortalDocument/PortalDocumentUploadService";
import { ConfirmationDialog } from "../../../Component/Common/Components/ConfirmationDialog";
import { toast } from "react-toastify";
import { ImportExportButtonsDialog } from "../../Common/ImportAndExport/ImportExportButtonsDialog";
import { DataImportType } from "../../../Component/Model/Enum/BEE123Enums";
import { ExportKeyValue } from "../../../Component/Model/Dto/Upload";
import { FilterType, IFilter } from "../../../Component/Common/Components/DataTable/Filters";

export const CertificateRegistryPage = () => {

    const [certificateRegistryData, setCertificateRegistryData] = useState<CertificateRegistry.CertificateRegistrySearchResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [searchValue, setSearchValue] = useState<string>('');
    const [filteredItemsCount, setFilteredItemsCount] = useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
    const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption[]>([]);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
    const [displayScorecardSelectionModal, setDisplayScorecardSelectionModal] = useState<boolean>(false);
    const [displayCertificateModal, setDisplayCertificateModal] = useState<boolean>(false);
    const [scorecardType, setScorecardType] = useState<ScorecardType>({'Id': 0, 'Name': ''});
    const [selectedCertificateId, setSelectedCertificateId] = useState<number>(0);
    const [filters, setFilters] = useState({});

    const confirmDeleteMessage = "This action cannot be reversed. Are you sure you want to delete the certificate registry entry?";

    const exportParameters: ExportKeyValue = {
        key: "SearchValue",
        value: searchValue
    }

    const certificateRegistrySearchRequest: CertificateRegistry.CertificateRegistrySearchRequest = {
        columnSortOptions: columnSortOptions,
        pageNumber: pageNumber,
        pageSize: entriesPerPage.defaultValue as number,
        searchValue: searchValue
    };

    const certFilters: Array<IFilter> = [
        {
          label: "Certificate Number:",
          type: FilterType.text,
          propname: "certificateNumber",
        },
        {
          label: "Version Number:",
          type: FilterType.text,
          propname: "versionNumber",
        }
    ];
    


    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const searchValueChanged = (value: string) => {
        setSearchValue(value);
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }

    const fetchData = async () => {
        setIsLoading(true);
        try {
            let response = await CertificateRegistryService.Search({...certificateRegistrySearchRequest, ...filters});
            setCertificateRegistryData(response.data.results);
            setFilteredItemsCount(response.data.filteredItemsCount);
            setTotalItemsCount(response.data.totalItemsCount);
        } catch (error) { 
            console.log(error);
        }
        finally {
            setIsLoading(false);
        }
    }

    const handleDelete = async () => {

        try {
            await CertificateRegistryService.DeleteCertificateEntry(selectedCertificateId);
            toast.success("Certificate entry deleted succesfully")
            fetchData();

        } catch (error:any) {
            if(error.response){
                toast.error(error.response.data);
            }
        }
        handleCloseConfirmation();

    }

    useEffect(() => {
        fetchData();
    }, [entriesPerPage, searchValue, pageNumber, columnSortOptions, filters]);

    const handleDownloadCertificate = async(documentId: number) => {
        try {
            const response = await PortalDocumentService.GetById(documentId);
            window.open(response.data);
        } catch (error) { 
            console.log(error);
        }
    }

    const handleEdit = (certificateId: number) => {
        setSelectedCertificateId(certificateId);
        setDisplayCertificateModal(true);
    }

    
    const handleConfirmDelete = (certificateId: number) => {
        setSelectedCertificateId(certificateId);
        setShowConfirmationDialog(true);
    }

    const handleAddCertificate = () => {
        setDisplayScorecardSelectionModal(true);
    }

    const handleCloseScorecardSelectionModal = () => {
        setDisplayScorecardSelectionModal(false);
    }

    const handleCloseCertificateModal = () => {
        setDisplayCertificateModal(false);
        setSelectedCertificateId(0);
        fetchData();
    }

    const handleCloseConfirmation = () => {
        setShowConfirmationDialog(false);
        setSelectedCertificateId(0);
    }

    const handleScorecardTypeSelect = (type: ScorecardType) => {
        handleCloseScorecardSelectionModal();
        setScorecardType(type);
        setDisplayCertificateModal(true);
    }

    const filtersChanged = async (filterProps: any) => {
        setPageNumber(1);
        setFilters({ ...filterProps})
    };

    const bulkActions = [
        {
            label: "Add Certificate",
            action: handleAddCertificate
        }
    ]

    const certificateRegistryTableData = CertificateRegistryTableMapper(certificateRegistryData, handleDownloadCertificate, handleEdit, handleConfirmDelete);

    return (
        <>
            <DataTable
                table={certificateRegistryTableData}
                entriesPerPage={entriesPerPage}
                pageNumber={pageNumber}
                filteredItemsCount={filteredItemsCount}
                totalItemsCount={totalItemsCount}
                entriesPerPageChanged={entriesPerPageChanged}
                pageNumberChanged={pageNumberChanged}
                isLoading={isLoading}
                canSearch={true}
                searchValueChanged={searchValueChanged}
                manualSorting={true}
                columnSortingChanged={columnSortingChanged}
                bulkActions={bulkActions}
                filters={certFilters}
                filtersChanged={filtersChanged}
            />

            { displayScorecardSelectionModal && 
                <ScorecardTypeSelectionModal 
                    showScorecardTypeSelectionModal={displayScorecardSelectionModal} 
                    handleCancel={handleCloseScorecardSelectionModal} 
                    onScorecardTypeSelect={handleScorecardTypeSelect}
                />
            }
            { displayCertificateModal && 
                <CertificateRegistryModal 
                    showModal={displayCertificateModal} 
                    scorecardType={scorecardType} 
                    id={selectedCertificateId !== 0 ? selectedCertificateId : undefined} 
                    handleCancel={handleCloseCertificateModal}
                />
            }

            <ConfirmationDialog
                showDialog={showConfirmationDialog}
                handleAccept={handleDelete}
                handleCancel={handleCloseConfirmation}
                heading={"Confirm Delete"}
                message={confirmDeleteMessage}
            />

            <ImportExportButtonsDialog
                type={"CertificateRegistry"}
                typeId={DataImportType.CertificateRegistry}
                uploadType={DataImportType[DataImportType.CertificateRegistry]}
                reloadTable={fetchData}
                parameters={[exportParameters]}
                controlsEnabled={true}
                showHelpfileDownload={true}
                importBus={true}
                coreExportEnabled={true}
                coreImportEnabled={true}
            />
        </>
    )
}
